import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Table,
    Button,
    Badge,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Col
} from "reactstrap";
import { getUserSessions, terminateAdminSession } from "api/ApiServices";
import Swal from "sweetalert2";
import Avatar from "components/Common/Avatar";

const SessionModal = ({ isOpen, toggle, user }) => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalSessions, setTotalSessions] = useState(0);
    const [sessionsPerPage] = useState(5);

    const fetchSessions = async (page = 1) => {
        if (!user?._id) return;
        setLoading(true);
        try {
            const res = await getUserSessions(user._id, page, sessionsPerPage);
            if (res.status && res.payload) {
                setSessions(res.payload.sessions || []);
                setTotalSessions(res.payload.total || 0);
            }
        } catch (error) {
            console.error("Error fetching sessions:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && user?._id) {
            fetchSessions(currentPage);
        }
    }, [isOpen, user?._id, currentPage]);

    const handleTerminate = async (sessionId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This session will be terminated!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, terminate it!',
            cancelButtonText: 'Cancel',

            didOpen: () => {
                const container = document.querySelector('.swal2-container');
                if (container) {
                    container.style.zIndex = '200000';
                }
            }
        });

        if (result.isConfirmed) {
            try {
                const response = await terminateAdminSession(sessionId);

                if (response.status) {
                    await Swal.fire({
                        title: 'Terminated!',
                        text: 'Session has been invalidated.',
                        icon: 'success',
                        timer: 1000,
                        showConfirmButton: false,

                        didOpen: () => {
                            const container = document.querySelector('.swal2-container');
                            if (container) {
                                container.style.zIndex = '200000';
                            }
                        }
                    });

                    fetchSessions(currentPage);
                }
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to terminate session',
                    icon: 'error',

                    didOpen: () => {
                        const container = document.querySelector('.swal2-container');
                        if (container) {
                            container.style.zIndex = '200000';
                        }
                    }
                });
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString();
    };

    const totalPages = Math.ceil(totalSessions / sessionsPerPage);

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
            className="modal-lg"
            contentClassName="border-0 shadow-lg"
            style={{ borderRadius: '20px', overflow: 'hidden' }}
            zIndex={1070}
        >
            <ModalHeader toggle={toggle} className="bg-white border-0 py-4">
                <div className="d-flex align-items-center">
                    <div className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow mr-3">
                        <i className="fas fa-history" />
                    </div>
                    <div>
                        <h4 className="mb-0 font-weight-bold">Active Hardware Sessions</h4>
                        <small className="text-muted">Managing connections for <span className="text-primary font-weight-bold">{user?.name}</span></small>
                    </div>
                </div>
            </ModalHeader>
            <ModalBody className="bg-secondary-soft px-0 pt-0">
                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table className="align-items-center table-flush mb-0" hover>
                        <thead className="thead-light">
                            <tr>
                                <th className="px-4 border-0">Device Environment</th>
                                <th className="border-0">IP Address</th>
                                <th className="border-0">Last Activity</th>
                                <th className="text-right px-4 border-0">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="text-center py-5">
                                        <i className="fas fa-spinner fa-spin text-primary mr-2" />
                                        <span className="text-muted">Retrieving live data...</span>
                                    </td>
                                </tr>
                            ) : sessions.length > 0 ? (
                                sessions.map((session) => (
                                    <tr key={session.as_uuid} className="bg-white">
                                        <td className="px-4">
                                            <div className="d-flex align-items-center">
                                                <div className={`icon icon-xs rounded-circle mr-2 ${session.as_status === 'active' ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger'}`}>
                                                    <i className={session.as_device?.toLowerCase().includes('mobile') ? 'fas fa-mobile-alt' : 'fas fa-desktop'} />
                                                </div>
                                                <div className="text-sm font-weight-bold text-dark text-truncate" style={{ maxWidth: '150px' }}>
                                                    {session.as_device || 'Unknown'}
                                                </div>
                                            </div>
                                            <small className="text-muted d-block text-xs">{session.as_location || 'Geo-Location Pending'}</small>
                                        </td>
                                        <td>
                                            <Badge color="secondary" className="px-2 py-1 text-dark" style={{ borderRadius: '6px' }}>
                                                {session.as_ip}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="text-xs text-dark">{formatDate(session.as_last_active)}</div>
                                            <Badge color={session.as_status === 'active' ? 'success' : 'danger'} pill className="text-xxs px-2" style={{ transform: 'scale(0.8)', marginLeft: '-5px' }}>
                                                {session.as_status}
                                            </Badge>
                                        </td>
                                        <td className="text-right px-4">
                                            {session.as_status === 'active' && (
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    className="btn-icon-only rounded-circle"
                                                    onClick={() => handleTerminate(session.as_uuid)}
                                                >
                                                    <i className="fas fa-power-off" />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center py-5 text-muted h6 font-weight-light">
                                        No active sessions detected for this user.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {totalPages > 1 && (
                    <div className="px-4 py-3 bg-white border-top d-flex justify-content-between align-items-center">
                        <small className="text-muted font-weight-bold">
                            SESSION {sessions.length} / {totalSessions}
                        </small>

                        <Pagination className="mb-0" size="sm">
                            {/* Previous Button */}
                            <PaginationItem disabled={currentPage === 1}>
                                <PaginationLink
                                    previous
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setCurrentPage(currentPage - 1);
                                    }}
                                />
                            </PaginationItem>

                            {/* Page Numbers with Ellipsis */}
                            {(() => {
                                const pages = [];
                                const delta = 2; // kitne pages current ke around dikhane hain

                                // Always show first page
                                pages.push(
                                    <PaginationItem key={1} active={currentPage === 1}>
                                        <PaginationLink
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setCurrentPage(1);
                                            }}
                                        >
                                            1
                                        </PaginationLink>
                                    </PaginationItem>
                                );

                                // Start Ellipsis
                                if (currentPage > delta + 2) {
                                    pages.push(
                                        <PaginationItem key="start-ellipsis" disabled>
                                            <PaginationLink href="#" onClick={(e) => e.preventDefault()}>
                                                ...
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                }

                                const rangeStart = Math.max(2, currentPage - delta);
                                const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

                                for (let i = rangeStart; i <= rangeEnd; i++) {
                                    pages.push(
                                        <PaginationItem key={i} active={currentPage === i}>
                                            <PaginationLink
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setCurrentPage(i);
                                                }}
                                            >
                                                {i}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                }

                                // End Ellipsis
                                if (currentPage < totalPages - delta - 1) {
                                    pages.push(
                                        <PaginationItem key="end-ellipsis" disabled>
                                            <PaginationLink href="#" onClick={(e) => e.preventDefault()}>
                                                ...
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                }

                                // Always show last page
                                if (totalPages > 1) {
                                    pages.push(
                                        <PaginationItem
                                            key={totalPages}
                                            active={currentPage === totalPages}
                                        >
                                            <PaginationLink
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setCurrentPage(totalPages);
                                                }}
                                            >
                                                {totalPages}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                }

                                return pages;
                            })()}

                            {/* Next Button */}
                            <PaginationItem disabled={currentPage === totalPages}>
                                <PaginationLink
                                    next
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setCurrentPage(currentPage + 1);
                                    }}
                                />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </ModalBody>
            <style>{`
                .bg-secondary-soft { background-color: #f8f9fe; }
                .bg-soft-success { background-color: rgba(45, 206, 137, 0.1); }
                .bg-soft-danger { background-color: rgba(245, 54, 92, 0.1); }
                .text-xxs { font-size: 0.65rem; }
                .btn-icon-only.rounded-circle { width: 30px; height: 30px; padding: 0; line-height: 30px; }
            `}</style>
        </Modal>
    );
};

export default SessionModal;
